import { Box, Button, CircularProgress } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  errorId: string
}

export function UnhandledErrorOverlay({ errorId }: Props) {
  const { t } = useTranslation()
  const [isReloading, setIsReloading] = useState(false)

  const handleReload = () => {
    setIsReloading(true)
    window.location.reload()
  }

  const handleReturnHome = () => {
    window.location.href = '/'
  }

  const supportLink = t('links.support')

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--color-scrim)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        color: 'white',
        padding: 2,
      }}
      role="alertdialog"
      aria-modal="true"
      aria-label={t('error.title')}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
        <Box sx={{ fontSize: '24px', fontWeight: 600, textAlign: 'center' }}>
          {t('error.title')}
        </Box>

        <Box sx={{ fontSize: '14px', textAlign: 'center', maxWidth: 400, lineHeight: 1.6 }}>
          {t('error.body')}
        </Box>

        {/* Error ID — reference for support */}
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ fontSize: '12px', color: 'var(--color-on-dark-60)', mb: 0.5 }}>
            {t('error.errorIdLabel')}
          </Box>
          <Box
            sx={{
              backgroundColor: 'var(--color-on-dark-10)',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontFamily: 'monospace',
              color: 'var(--color-white)',
              letterSpacing: '0.04em',
              userSelect: 'all',
            }}
          >
            {errorId}
          </Box>
          <Box sx={{ fontSize: '11px', color: 'var(--color-on-dark-45)', mt: 0.5 }}>
            {t('error.errorIdHint')}
          </Box>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            onClick={handleReload}
            disabled={isReloading}
            sx={{
              borderColor: 'white',
              color: 'white',
              '&:hover': { backgroundColor: 'var(--color-on-dark-10)' },
              '&:disabled': { borderColor: 'var(--color-on-dark-50)', color: 'var(--color-on-dark-50)' },
            }}
          >
            {isReloading ? <CircularProgress size={20} sx={{ color: 'inherit' }} /> : t('error.refreshPage')}
          </Button>

          <Button
            variant="outlined"
            onClick={handleReturnHome}
            sx={{
              borderColor: 'white',
              color: 'white',
              '&:hover': { backgroundColor: 'var(--color-on-dark-10)' },
            }}
          >
            {t('error.returnHome')}
          </Button>

          <Button
            variant="outlined"
            component="a"
            href={supportLink}
            sx={{
              borderColor: 'white',
              color: 'white',
              textDecoration: 'none',
              '&:hover': { backgroundColor: 'var(--color-on-dark-10)' },
            }}
          >
            {t('error.contactSupport')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
